import { getProductPermalink } from '~/lib'
import { useSeoUrlStore } from '~/store/seoUrlStore'
import type { Product } from '~/types/shopware'
import { getProductTranslatedCustomFields } from '~/types/shopware'

export interface CustomFields {
  custom_product_fields_subtitle: string
  custom_product_fields_edition: string
  custom_product_fields_edition_statement: string
  custom_product_fields_binding: string
  custom_product_fields_weight: string
  custom_product_fields_page_count: string
  custom_product_fields_dimensions: string
  custom_product_fields_editor: string
  custom_product_fields_publisher: string
  custom_product_fields_product_type_number: string
  custom_product_fields_update_link: string
  custom_product_fields_other_text: string
}

export interface ProductVariation {
  id: string
  url: string
  price: string
  name: string
  type: string
}

export interface CrossSellingProduct {
  id: string
  order: number
  slug: string
  label: string
}

interface PropertyCustomFields {
  custom_property_fields_type_property_group_code?: string
  custom_property_fields_type_property_code?: string
}

interface ProductProperty {
  name: string
  customFields: PropertyCustomFields | null
  translated?: {
    customFields?: PropertyCustomFields | null
  }
}

interface CrossSellingItem {
  crossSelling: { type?: string }
  products: Product[]
}

/**
 * Get property custom fields using translation inheritance.
 * In non-default Shopware languages, `customFields` on properties can be null
 * while `translated.customFields` contains the inherited values.
 */
function getPropertyCustomFields(prop: ProductProperty): PropertyCustomFields | null {
  return prop.translated?.customFields ?? prop.customFields ?? null
}

export const useCrossSellingProducts = () => {
  const { apiClient } = useShopwareContext()
  const { getFormattedPrice } = usePrice()
  const { t } = useI18n()
  const { languageCode } = useLocale()

  const PRODUCT_LANGUAGES = {
    ger: {
      label: t('shop.productPage.languageVariations.german'),
      order: 1,
    },
    fre: {
      label: t('shop.productPage.languageVariations.french'),
      order: 2,
    },
    ita: {
      label: t('shop.productPage.languageVariations.italian'),
      order: 3,
    },
    eng: {
      label: t('shop.productPage.languageVariations.english'),
      order: 4,
    },
  }

  const getCrossSelling = async (product: Product): Promise<CrossSellingItem[] | null> => {
    try {
      const response = await apiClient.invoke('readProductCrossSellings post /product/{productId}/cross-selling', {
        pathParams: {
          productId: product.id,
        },
      })
      // The API returns CrossSellingElementCollection which is an array directly
      return response.data || []
    } catch {
      return null
    }
  }

  const getVariants = async (product: Product, customFields: CustomFields) => {
    const { data: variants } = await useAsyncData(
      'productVariants' + product.id + '-' + languageCode.value,
      async () => {
        const response = await apiClient.invoke('readProduct post /product', {
          body: {
            filter: [
              {
                type: 'equals',
                field: 'customFields.custom_product_fields_product_type_number',
                value: customFields.custom_product_fields_product_type_number,
              },
            ],
            associations: {
              properties: {},
              seoUrls: {},
            },
          },
        })

        const variants: ProductVariation[] = []

        response.data.elements.forEach((item: unknown) => {
          const product = item as Product
          const property = product.properties?.find(
            (prop: unknown) =>
              getPropertyCustomFields(prop as ProductProperty)?.custom_property_fields_type_property_group_code ===
              'product-type-group',
          ) as ProductProperty | undefined
          const propertyFields = property ? getPropertyCustomFields(property) : null
          variants.push({
            id: product.id,
            url: getProductPermalink(product),
            price: getFormattedPrice(product.calculatedPrice.totalPrice),
            name: property?.name ?? '',
            type: propertyFields?.custom_property_fields_type_property_code ?? '',
          })
        })

        return variants
      },
    )
    return variants
  }

  const getCrossSellingProducts = async (product: Product) => {
    const { data: crossSellingProducts } = await useAsyncData(
      'productCrossSelling' + product.id + '-' + languageCode.value,
      async () => {
        const seoStore = useSeoUrlStore()
        const languageVariations: CrossSellingProduct[] = []
        let relatedProducts: Product[] = []
        let relatedProductsReplaced: Product[] = []
        let productSetProducts: Product[] = []
        let containedProducts: Product[] = []
        const response = await getCrossSelling(product)

        if (!response) {
          return null
        }

        response.forEach((item: CrossSellingItem) => {
          if (item.crossSelling.type === 'language_related_products') {
            item.products.forEach((product: Product) => {
              const customFields = getProductTranslatedCustomFields(product)
              const languageCode = customFields?.custom_product_first_language_code as
                keyof typeof PRODUCT_LANGUAGES | undefined
              if (languageCode) {
                languageVariations.push({
                  id: product.id,
                  order: PRODUCT_LANGUAGES[languageCode]?.order,
                  slug: '', // set later after ensuring SEO URLs
                  label: PRODUCT_LANGUAGES[languageCode]?.label,
                })
              }
            })
          } else if (item.crossSelling.type === 'product_sets') {
            productSetProducts = productSetProducts.concat(item.products)
          } else if (item.crossSelling.type === 'contained_products') {
            containedProducts = containedProducts.concat(item.products)
          } else if (item.crossSelling.type === 'related_products') {
            relatedProducts = relatedProducts.concat(item.products)
          } else if (item.crossSelling.type === 'related_products_replaced') {
            relatedProductsReplaced = relatedProductsReplaced.concat(item.products)
          }
        })
        // We also add the current product to the list.
        const customFields = getProductTranslatedCustomFields(product)
        const languageCode = customFields?.custom_product_first_language_code as keyof typeof PRODUCT_LANGUAGES
        if (languageCode) {
          languageVariations.push({
            id: product.id,
            order: PRODUCT_LANGUAGES[languageCode]?.order,
            slug: '',
            label: PRODUCT_LANGUAGES[languageCode]?.label,
          })
        }
        languageVariations.sort((a, b) => (a.order > b.order ? 1 : -1))

        // Ensure SEO URLs for all product ids we will render links for
        const allIds = new Set<string>()
        languageVariations.forEach((p) => allIds.add(p.id))
        relatedProducts.forEach((p) => allIds.add(p.id))
        relatedProductsReplaced.forEach((p) => allIds.add(p.id))
        productSetProducts.forEach((p) => allIds.add(p.id))
        containedProducts.forEach((p) => allIds.add(p.id))
        if (allIds.size) await seoStore.ensureProductUrls([...allIds])

        // Populate slugs for the language variations now that cache is warm
        languageVariations.forEach((lv) => {
          lv.slug =
            seoStore.getProductUrlById(lv.id) || getProductPermalink({ id: lv.id, seoUrls: [] } as unknown as Product)
        })

        return { languageVariations, relatedProducts, productSetProducts, containedProducts, relatedProductsReplaced }
      },
    )

    return crossSellingProducts
  }
  return {
    getVariants,
    getCrossSellingProducts,
  }
}
